import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { REPORT_REASONS, reportContent } from '../lib/report';
import './ReportModal.css';

// Report / flag offensive AI-generated content. Rendered via portal so it
// floats above whatever surface triggered it (image card, chat bubble…).
//
// Props:
//   open        - whether the modal is visible
//   onClose     - called when the user dismisses / after the thank-you
//   kind        - 'image' | 'chat' | 'companion'  (passed to reportContent)
//   content     - the offending text/prompt, when available
//   meta        - extra context object (model, style, batch id…)
export default function ReportModal({ open, onClose, kind, content = '', meta = {} }) {
  const [reason, setReason]   = useState('');
  const [details, setDetails] = useState('');
  const [state, setState]     = useState('form');   // 'form' | 'sending' | 'done'

  // Reset every time the modal is (re)opened so a prior report's state
  // doesn't leak into the next one.
  useEffect(() => {
    if (open) { setReason(''); setDetails(''); setState('form'); }
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!reason || state !== 'form') return;
    setState('sending');
    await reportContent({ kind, reason, details, content, meta });
    setState('done');
    // Auto-dismiss the thank-you after a beat.
    setTimeout(() => onClose?.(), 1600);
  };

  return ReactDOM.createPortal(
    <div className="report-overlay" onClick={onClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Report content">
        {state === 'done' ? (
          <div className="report-done">
            <div className="report-done-check" aria-hidden>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="report-done-title">Thanks for reporting</div>
            <div className="report-done-sub">
              Our team will review this content and take action if it
              violates our policies.
            </div>
          </div>
        ) : (
          <>
            <div className="report-head">
              <div className="report-title">Report this content</div>
              <button className="report-x" onClick={onClose} aria-label="Close">×</button>
            </div>
            <div className="report-sub">
              Tell us what's wrong with this AI-generated content. Reports
              are confidential.
            </div>

            <div className="report-reasons">
              {REPORT_REASONS.map((r) => (
                <label key={r.id} className={`report-reason ${reason === r.id ? 'on' : ''}`}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                  />
                  <span className="report-reason-dot" aria-hidden />
                  <span className="report-reason-label">{r.label}</span>
                </label>
              ))}
            </div>

            <textarea
              className="report-details"
              placeholder="Add any details (optional)"
              rows={2}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={2000}
            />

            <div className="report-actions">
              <button className="report-cancel" onClick={onClose}>Cancel</button>
              <button
                className="report-submit"
                onClick={submit}
                disabled={!reason || state === 'sending'}
              >
                {state === 'sending' ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
